import { useCallback, useEffect, useRef, useState } from "react";

function SlotMachineDigit({
  digit,
  delay,
  onComplete,
}: {
  digit: string;
  delay: number;
  onComplete?: () => void;
}) {
  const [displayDigit, setDisplayDigit] = useState("0");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (digit === "," || digit === "$") {
      setDisplayDigit(digit);
      onComplete?.();
      return;
    }

    const targetNum = parseInt(digit, 10);
    if (isNaN(targetNum)) {
      setDisplayDigit(digit);
      onComplete?.();
      return;
    }

    let currentIteration = 0;
    const totalIterations = 15 + Math.floor(Math.random() * 10);
    const intervalTime = 40;

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        currentIteration++;
        if (currentIteration < totalIterations) {
          setDisplayDigit(Math.floor(Math.random() * 10).toString());
        } else {
          setDisplayDigit(digit);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          onComplete?.();
        }
      }, intervalTime);
    }, delay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [digit, delay, onComplete]);

  return (
    <span className="inline-block tabular-nums transition-transform">
      {displayDigit}
    </span>
  );
}

export function SlotMachineCurrency({
  value,
  trigger,
  onAnimationComplete,
  formatFn,
}: {
  value: number;
  trigger: boolean;
  onAnimationComplete?: () => void;
  formatFn: (v: number) => string;
}) {
  const formatted = formatFn(value);
  const [key, setKey] = useState(0);
  const completedCount = useRef(0);
  const totalDigits = formatted.length;
  const hasCalledComplete = useRef(false);
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (trigger && !hasTriggered.current) {
      hasTriggered.current = true;
      setKey((k) => k + 1);
      completedCount.current = 0;
      hasCalledComplete.current = false;
    }
  }, [trigger]);

  const handleDigitComplete = useCallback(() => {
    completedCount.current++;
    if (completedCount.current >= totalDigits && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      onAnimationComplete?.();
    }
  }, [totalDigits, onAnimationComplete]);

  if (!trigger) {
    return <span className="invisible">{formatted}</span>;
  }

  return (
    <span className="inline-flex" key={key}>
      {formatted.split("").map((char, index) => (
        <SlotMachineDigit
          key={`${key}-${index}`}
          digit={char}
          delay={index * 60}
          onComplete={handleDigitComplete}
        />
      ))}
    </span>
  );
}
